import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Card, CardContent } from "./ui/card";
import { Flame, Trophy } from "lucide-react";

export function StreakBoard({ canPickBatch = false }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [batchId, setBatchId] = useState("");

  useEffect(() => {
    api.get(`/streak/board${batchId ? `?batch_id=${batchId}` : ""}`)
      .then(({ data }) => setData(data.data))
      .catch((e) => toast.error(extractError(e).message));
  }, [batchId]);

  if (!data) return null;
  if (!data.batch) return null;

  return (
    <Card data-testid="streak-board" className="bg-[#10121B] border-white/5">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base md:text-lg font-semibold text-white inline-flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-300" /> {t("streak.boardTitle")}
          </h2>
          {canPickBatch && data.batches.length > 0 ? (
            <select data-testid="streak-board-batch" value={batchId || data.batch.batch_id} onChange={(e) => setBatchId(e.target.value)}
              className="h-9 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
              {data.batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{b.code} · {b.start_date}</option>)}
            </select>
          ) : (
            <span className="text-xs text-slate-500">{data.batch.code}</span>
          )}
        </div>

        {data.rows.length === 0 ? (
          <p className="text-sm text-slate-500">{t("streak.boardEmpty")}</p>
        ) : (
          <div className="space-y-2">
            {data.rows.map((r) => (
              <div key={r.user_id} data-testid={`streak-row-${r.user_id}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${r.is_me ? "border-[#FF3B30]/40 bg-[#FF3B30]/[0.07]" : "border-white/5 bg-white/[0.02]"}`}>
                <span className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center font-[Outfit] font-extrabold text-sm ${r.rank <= 3 ? "bg-amber-500/15 text-amber-300" : "bg-white/5 text-slate-400"}`}>
                  {r.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">
                    {r.display_name}
                    {r.badge && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-300">{r.badge}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {r.coach_name ? `${t("streak.coach")}: ${r.coach_name}` : t("streak.noCoach")} · {r.completed_education_days} {t("streak.daysDone")}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="inline-flex items-center gap-1 font-[Outfit] font-extrabold text-white">
                    <Flame className={`h-4 w-4 ${r.current_streak > 0 ? "text-[#FF3B30]" : "text-slate-600"}`} /> {r.current_streak}
                  </div>
                  <div className="text-[10px] text-slate-500">{t("streak.record")} {r.longest_streak}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
